// src/contexts/AuthContext.jsx
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { auth, db, ensureAuthTenant } from '../firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import {
  validateToken as validateSessionToken,
  subscribeToSession,
  getSessionErrorMessage
} from '../services/sessionService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // Firebase Auth state
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Session state
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // Client state
  const [client, setClient] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  // Entitlement state
  const [entitlement, setEntitlement] = useState(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);

  // Refs to store Firestore unsubscribe functions
  const sessionUnsubscribeRef = useRef(null);
  const clientUnsubscribeRef = useRef(null);
  const entitlementUnsubscribeRef = useRef(null);

  // Sign up with email and password
  const signup = async (email, password) => {
    try {
      setError(null);
      ensureAuthTenant();
      const result = await createUserWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with email and password
  const login = async (email, password) => {
    try {
      setError(null);
      ensureAuthTenant();
      const result = await signInWithEmailAndPassword(auth, email, password);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    try {
      setError(null);
      ensureAuthTenant();
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Validate token with backend API
  const validateToken = async (token, userId) => {
    try {
      setSessionError(null);
      setSessionLoading(true);

      // Call backend API to validate token and create session
      // Backend also syncs client_id/role onto Firebase custom claims
      const result = await validateSessionToken(token, userId);

      // Force-refresh ID token so new custom claims (client_id) are present
      try {
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true);
          const tokenResult = await auth.currentUser.getIdTokenResult();
          console.log('✓ Token claims after session sync:', {
            client_id: tokenResult.claims?.client_id || null,
            role: tokenResult.claims?.role || null,
            session_type: tokenResult.claims?.session_type || null,
          });
        }
      } catch (claimsErr) {
        console.warn('Token claim refresh failed (non-fatal):', claimsErr);
      }

      console.log('✓ Token validated successfully:', result);
      return result;
    } catch (err) {
      const friendlyError = getSessionErrorMessage(err);
      setSessionError(friendlyError);
      console.error('Session validation failed:', err);
      throw new Error(friendlyError);
    } finally {
      setSessionLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      setError(null);

      // Clean up session subscription
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }

      // Clean up client subscription
      if (clientUnsubscribeRef.current) {
        clientUnsubscribeRef.current();
        clientUnsubscribeRef.current = null;
      }

      // Clean up entitlement subscription
      if (entitlementUnsubscribeRef.current) {
        entitlementUnsubscribeRef.current();
        entitlementUnsubscribeRef.current = null;
      }

      // Clear session, client, and entitlement state
      setSession(null);
      setSessionError(null);
      setClient(null);
      setEntitlement(null);

      await firebaseSignOut(auth);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Listen to Firebase auth state changes
  useEffect(() => {
    ensureAuthTenant();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        console.log('[Auth] signed in', {
          email: user.email,
          uid: user.uid,
          userTenantId: user.tenantId || '(none — Default tenant)',
          authTenantId: auth.tenantId || '(not set)',
        });
      }
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // On auth / page refresh: ensure session API runs so backend syncs custom claims,
  // then force-refresh ID token to pick up client_id.
  useEffect(() => {
    if (!currentUser) return undefined;

    let cancelled = false;

    (async () => {
      try {
        const token = await currentUser.getIdToken();
        await validateSessionToken(token, currentUser.uid);
        if (cancelled) return;
        await currentUser.getIdToken(true);
        const tokenResult = await currentUser.getIdTokenResult();
        console.log('✓ Auth claims synced:', {
          client_id: tokenResult.claims?.client_id || null,
          role: tokenResult.claims?.role || null,
        });
      } catch (err) {
        if (!cancelled) {
          console.warn('Background session/claims sync failed:', err?.message || err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid]);

  // Subscribe to Firestore session document when user is authenticated
  useEffect(() => {
    if (!currentUser) {
      // Clean up subscription when user logs out
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
      setSession(null);
      setSessionError(null);
      return;
    }

    // Set up Firestore subscription
    console.log('📡 Setting up session subscription for user:', currentUser.uid);

    const unsubscribe = subscribeToSession(
      currentUser.uid,
      (sessionData) => {
        console.log('📊 Session update received:', sessionData);
        setSession(sessionData);
        setSessionLoading(false);

        // Check if session is invalid/revoked
        if (sessionData.exists && sessionData.status === 'revoked') {
          setSessionError('Your session has been revoked. Please sign in again.');
        }
      },
      (error) => {
        console.error('Session subscription error:', error);
        setSessionError('Failed to connect to session service.');
        setSessionLoading(false);
      }
    );

    // Store unsubscribe function
    sessionUnsubscribeRef.current = unsubscribe;

    // Cleanup on unmount
    return () => {
      if (sessionUnsubscribeRef.current) {
        sessionUnsubscribeRef.current();
        sessionUnsubscribeRef.current = null;
      }
    };
  }, [currentUser]);

  // Subscribe to Firestore client document when session has client reference
  useEffect(() => {
    // Clean up previous client subscription
    if (clientUnsubscribeRef.current) {
      clientUnsubscribeRef.current();
      clientUnsubscribeRef.current = null;
    }

    // Clear client data when reference changes
    setClient(null);

    // If no client reference, nothing to subscribe to
    if (!session?.client_ref) {
      setClientLoading(false);
      return;
    }

    // Convert serialized reference to Firestore DocumentReference if needed
    let clientRef = session.client_ref;

    // Check if it's a serialized reference (has referencePath property)
    if (clientRef?.referencePath && typeof clientRef.referencePath === 'string') {
      clientRef = doc(db, clientRef.referencePath);
    }

    // Set up Firestore subscription to client document
    console.log('📡 Setting up client subscription');
    setClientLoading(true);

    const unsubscribe = onSnapshot(
      clientRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          console.log('📊 Client update received:', docSnapshot.id);
          setClient({
            ...docSnapshot.data(),
            id: docSnapshot.id,
          });
        } else {
          console.warn('Client document does not exist');
          setClient(null);
        }
        setClientLoading(false);
      },
      (error) => {
        console.error('Client subscription error:', error);
        setClient(null);
        setClientLoading(false);
      }
    );

    // Store unsubscribe function
    clientUnsubscribeRef.current = unsubscribe;

    // Cleanup on unmount or when client reference changes
    return () => {
      if (clientUnsubscribeRef.current) {
        clientUnsubscribeRef.current();
        clientUnsubscribeRef.current = null;
      }
    };
  }, [session?.client_ref]); // React to client reference changes

  // Subscribe to Firestore entitlement document when client has current_entitlement reference
  useEffect(() => {
    // Clean up previous entitlement subscription
    if (entitlementUnsubscribeRef.current) {
      entitlementUnsubscribeRef.current();
      entitlementUnsubscribeRef.current = null;
    }

    // Clear entitlement data when reference changes
    setEntitlement(null);

    // If no entitlement reference, nothing to subscribe to
    if (!client?.current_entitlement_ref) {
      setEntitlementLoading(false);
      return;
    }

    // Convert serialized reference to Firestore DocumentReference if needed
    let entitlementRef = client.current_entitlement_ref;

    // Check if it's a serialized reference (has referencePath property)
    if (entitlementRef?.referencePath && typeof entitlementRef.referencePath === 'string') {
      entitlementRef = doc(db, entitlementRef.referencePath);
    }

    // Set up Firestore subscription to entitlement document
    console.log('📡 Setting up entitlement subscription');
    setEntitlementLoading(true);

    const unsubscribe = onSnapshot(
      entitlementRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          console.log('📊 Entitlement update received:', docSnapshot.id);
          setEntitlement({
            ...docSnapshot.data(),
            id: docSnapshot.id,
          });
        } else {
          console.warn('Entitlement document does not exist');
          setEntitlement(null);
        }
        setEntitlementLoading(false);
      },
      (error) => {
        console.error('Entitlement subscription error:', error);
        setEntitlement(null);
        setEntitlementLoading(false);
      }
    );

    // Store unsubscribe function
    entitlementUnsubscribeRef.current = unsubscribe;

    // Cleanup on unmount or when entitlement reference changes
    return () => {
      if (entitlementUnsubscribeRef.current) {
        entitlementUnsubscribeRef.current();
        entitlementUnsubscribeRef.current = null;
      }
    };
  }, [client?.current_entitlement_ref]); // React to entitlement reference changes

  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    loginWithGoogle,
    error,
    loading,
    // Session properties
    session,
    sessionLoading,
    sessionError,
    validateToken,
    // Client properties
    client,
    clientLoading,
    // Entitlement properties
    entitlement,
    entitlementLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
